import { Platform } from 'react-native';
import { OAuthProvider, signInWithCredential, updateProfile } from 'firebase/auth';
import * as AppleAuthentication from 'expo-apple-authentication';
import * as Crypto from 'expo-crypto';
import { auth } from '@/lib/firebase';
import { AccountLinkingRequiredError, getEmailFromIdToken } from './accountLinking';

/**
 * Check if Apple Sign-In is available on this device
 */
export async function isAppleSignInAvailable(): Promise<boolean> {
  if (Platform.OS !== 'ios') return false;
  try {
    return await AppleAuthentication.isAvailableAsync();
  } catch {
    return false;
  }
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Apple only returns full name on the first authorization; persist via Firebase displayName. */
function formatAppleDisplayName(
  fullName: AppleAuthentication.AppleAuthenticationFullName | null,
): string | null {
  if (!fullName) return null;
  const parts = [fullName.givenName, fullName.familyName].filter(
    (p): p is string => typeof p === 'string' && p.trim().length > 0,
  );
  const joined = parts.join(' ').trim();
  return joined.length > 0 ? joined : null;
}

/**
 * Sign in with Apple using Firebase Authentication
 * Only available on iOS 13+
 */
export async function signInWithApple(): Promise<void> {
  try {
    // Check if Apple Sign-In is available
    const available = await isAppleSignInAvailable();
    if (!available) {
      throw new Error('Apple Sign-In is not available on this device');
    }

    // Generate nonce for Firebase (recommended)
    // Firebase expects the raw nonce in the credential, while Apple receives the SHA256(nonce).
    const rawNonce = bytesToHex(await Crypto.getRandomBytesAsync(16));
    const hashedNonce = await Crypto.digestStringAsync(Crypto.CryptoDigestAlgorithm.SHA256, rawNonce);

    // Request Apple credential
    const credential = await AppleAuthentication.signInAsync({
      requestedScopes: [
        AppleAuthentication.AppleAuthenticationScope.FULL_NAME,
        AppleAuthentication.AppleAuthenticationScope.EMAIL,
      ],
      nonce: hashedNonce,
    });

    // Check if user cancelled
    if (!credential.identityToken) {
      throw new Error('Apple Sign-In was cancelled or failed');
    }

    // Create Firebase OAuth provider
    const provider = new OAuthProvider('apple.com');
    
    // Create Firebase credential from Apple identity token
    const firebaseCredential = provider.credential({
      idToken: credential.identityToken,
      rawNonce,
    });

    try {
      await signInWithCredential(auth, firebaseCredential);
    } catch (signInError: unknown) {
      const err = signInError as { code?: string; customData?: { email?: string } };
      if (err?.code === 'auth/credential-already-in-use' && credential.identityToken) {
        const email =
          (err.customData?.email as string | undefined)?.trim() ||
          getEmailFromIdToken(credential.identityToken) ||
          '';
        if (email) {
          throw new AccountLinkingRequiredError(email, firebaseCredential, 'Apple');
        }
      }
      throw signInError;
    }

    const displayName = formatAppleDisplayName(credential.fullName ?? null);
    if (displayName && auth.currentUser) {
      try {
        await updateProfile(auth.currentUser, { displayName });
      } catch (profileErr) {
        console.warn('[AppleAuth] updateProfile failed (sign-in still succeeded):', profileErr);
      }
    }
  } catch (error) {
    if (error instanceof AccountLinkingRequiredError) throw error;
    console.error('[AppleAuth] Sign-in error:', error);

    if (error instanceof Error && error.message.includes('cancel')) {
      throw new Error('Apple Sign-In was cancelled');
    }

    if (error instanceof Error) throw error;
    throw new Error('Failed to sign in with Apple');
  }
}

